import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import Api from "src/Config";
import { createContext, FunctionComponent, ReactElement, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { textTruncate } from "src/helpers/truncateText";

export const RadarProfileContext = createContext(null);

type Props = {
  children: ReactElement;
};

const score_color: any = {
  LOW: "#05CD99",
  MEDIUM: "#F8961E",
  HIGH: "#F94144",
};

const score_bgcolor: any = {
  LOW: "#EBF9F1",
  MEDIUM: "#FEF2E5",
  HIGH: "#FBE7E8",
};

const RadarProfileProvider: FunctionComponent<Props> = ({ children }) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const { pathname } = useLocation();
  const pageQuery = queryParams.get("page") || 1;
  const tabQuery = queryParams.get("tab");

  const [searchParam, setSearchParam] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["check_history", searchValue, pageQuery, queryParams.toString()],
    staleTime: 30000,
    enabled: tabQuery === "single" || pathname === "/app/dashboard",
    queryFn: async () => {
      const url = searchValue
        ? `onboarding/profiles?search=${searchValue}&page_size=10&page=1`
        : `onboarding/profiles?page_size=10&${decodeURIComponent(
            queryParams as any,
          )}`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          first_name: string;
          last_name: string;
          other_name: string;
          source: string;
          created_at: string;
          email: string;
          risk_status: string;
          risk_score: number;
          business_name: string;
        }) => ({
          id: res.id,
          name: `${
            !res.first_name?.toLowerCase() &&
            !res.last_name?.toLowerCase() &&
            !res.other_name?.toLowerCase() &&
            !res.business_name?.toLowerCase()
              ? "N/A"
              : res.business_name &&
                  !res.first_name?.toLowerCase() &&
                  !res.last_name?.toLowerCase() &&
                  !res.other_name?.toLowerCase()
                ? textTruncate(res.business_name, 20)
                : textTruncate(
                    `${res.first_name?.toLowerCase() || ""} ${
                      res.other_name?.toLowerCase() || ""
                    } ${res.last_name?.toLowerCase() || ""}`,
                    20,
                  )
          }`,
          source: `${res.source?.toLowerCase()} Check`,
          date: format(new Date(res.created_at), "dd/MM/yyyy"),
          "email address": res.email?.toLowerCase() || "N/A",
          status: (
            <div
              style={{
                color: score_color[res.risk_status],
                background: score_bgcolor[res.risk_status],
                width: "fit-content",
                textTransform: "lowercase",
                padding: "2px 15px",
                borderRadius: "23.29px",
              }}
            >
              {res.risk_status}
            </div>
          ),
          "risk score": (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div
                style={{
                  background: "#D9DDE3",
                  width: "100%",
                  borderRadius: "4px",
                }}
              >
                <div
                  style={{
                    background: score_color[res.risk_status],
                    height: "1.2vh",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                    width: res.risk_score,
                  }}
                />
              </div>
              {res.risk_score.toFixed(2)}%
            </div>
          ),
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      // setResponse(data?.results);
      setPageCount(Math.ceil(data?.metadata.count / 10));
      // setPageCount(10);
    }
  }, [data]);

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  // const handlePaginate = ({ selected }: any) => {
  //   setPageNumber(selected + 1);
  // };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      handleSearch,
      searchParam,
      isFetching,
      // handlePaginate,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearchRecord,
      error,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      // handlePaginate,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearchRecord,
    ],
  );
  return (
    <RadarProfileContext.Provider value={contextValue}>
      {children}
    </RadarProfileContext.Provider>
  );
};

export default RadarProfileProvider;
